import React, { memo, useCallback, useState } from "react";

import {
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spacer,
  Spinner,
  Switch,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";

import { useMutation } from "@apollo/client";
import ObjectID from "bson-objectid";
import { EditMeMutation, EditMeMutationVariables } from "gql/graphql";
import { BellOutlineSlashIcon, CalendarIcon, NotesIcon } from "modules/icons";
import { useAuth } from "modules/providers/auth-provider";
import { useOverlay } from "modules/providers/overlay-provider";
import moment from "moment-timezone";
import { FieldValues, useForm } from "react-hook-form";
import colors from "app/colors";
import { getMessageFromError } from "modules/utils/Functions";
import EDIT_ME_MUTATION from "modules/hooks/_networking/edit-me";
import { useMixpanel } from "../providers/mixpanel-provider";
import { MIXPANEL_EVENT_TYPE } from "../providers/mixpanel-provider/mixpanel-provider";

const OutOfOfficeModal = () => {
  const { me } = useAuth();
  const { closeModal } = useOverlay();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { track } = useMixpanel();

  const outOfOffice = me?.outOfOffice;

  const [editMeMutationAPI] = useMutation<
    EditMeMutation,
    EditMeMutationVariables
  >(EDIT_ME_MUTATION);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
    watch,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      startTime: outOfOffice?.startTime
        ? moment.parseZone(outOfOffice?.startTime).format("YYYY-MM-DD")
        : undefined,
      endTime: outOfOffice?.endTime
        ? moment.parseZone(outOfOffice?.endTime).format("YYYY-MM-DD")
        : undefined,
      message: outOfOffice?.message || "",
      isMuteNotifications: outOfOffice?.isMuteNotifications ?? true,
    },
  });

  const startTime = watch("startTime");

  const onSubmit = useCallback(
    async ({
      startTime,
      endTime,
      message,
      isMuteNotifications,
    }: FieldValues) => {
      if (!me) return;
      if (!startTime) {
        setError("startTime", {
          type: "required",
          message: "A start time is required",
        });
        return;
      }
      // https://teamcake.atlassian.net/browse/SYN-2175
      // Hack to set the start time to start of the PST; will need to be fixed in the future
      // Hack to set the end time to end of the EST; will need to be fixed in the future
      const _startTime = moment
        .tz(startTime, "America/Los_Angeles")
        .startOf("day")
        .toISOString();
      const _endTime = endTime
        ? moment.tz(endTime, "America/New_York").endOf("day").toISOString()
        : undefined;

      setIsSubmitting(true);
      editMeMutationAPI({
        variables: {
          input: {
            outOfOffice: {
              startTime: _startTime,
              endTime: _endTime,
              message,
              isMuteNotifications,
            },
          },
        },
        optimisticResponse: {
          editMe: {
            ...me,
            outOfOffice: {
              id: new ObjectID().toHexString(),
              startTime: _startTime,
              endTime: _endTime,
              message,
              isMuteNotifications,
            },
          },
        },
        onError: (error: Error) => {
          setIsSubmitting(false);
          setError("root", {
            message: error.message,
          });
        },
        onCompleted: () => {
          closeModal();
        },
      });
      track(MIXPANEL_EVENT_TYPE.OUT_OF_OFFICE_SET, {
        "Out of Office Set": true,
        "Message Set": message != null,
        "Notifications Muted": isMuteNotifications,
      });
    },
    [closeModal, editMeMutationAPI, me, setError, track],
  );

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalCloseButton left={5} top={6} />
        <ModalBody>
          <form id="out-of-office-form" onSubmit={handleSubmit(onSubmit)}>
            <VStack w="full" spacing={6} py={4}>
              <Text
                fontFamily="Outfit"
                fontSize={20}
                fontWeight={700}
                color={colors.blackSecondary}
              >
                Out of Office Mode Settings
              </Text>
              <VStack w="full" spacing={1}>
                <HStack w="full" spacing={1}>
                  <CalendarIcon fill={colors.blackSecondary} boxSize={5} />
                  <Text
                    fontSize={18}
                    fontWeight={600}
                    color={colors.blackSecondary}
                  >
                    Date Range
                  </Text>
                </HStack>
                <HStack w="full" alignItems="flex-start">
                  <FormControl isInvalid={!!errors?.startTime}>
                    <VStack spacing={0} w="full" alignItems="flex-start">
                      <FormLabel
                        fontSize={14}
                        fontWeight={400}
                        color={colors.grayMain}
                        mb={1}
                      >
                        First Day
                      </FormLabel>
                      <Input
                        id="startTime"
                        type="date"
                        size="sm"
                        {...register("startTime", {
                          required: {
                            message: "The start time is required",
                            value: true,
                          },
                        })}
                      />
                      <FormErrorMessage>
                        {getMessageFromError(errors?.startTime?.message)}
                      </FormErrorMessage>
                    </VStack>
                  </FormControl>
                  <FormControl isInvalid={!!errors?.endTime}>
                    <VStack spacing={0} w="full" alignItems="flex-start">
                      <FormLabel
                        fontSize={14}
                        fontWeight={400}
                        color={colors.grayMain}
                        mb={1}
                      >
                        Last Day (Optional)
                      </FormLabel>
                      <Input
                        id="endTime"
                        type="date"
                        size="sm"
                        min={moment
                          .max(moment(startTime), moment())
                          .format("YYYY-MM-DD")}
                        {...register("endTime", {
                          validate: (value) => {
                            if (!value) return true;
                            return (
                              moment(value).isSameOrAfter(moment(startTime)) ||
                              "Must be after the first day"
                            );
                          },
                        })}
                      />
                      <FormErrorMessage>
                        {getMessageFromError(errors?.endTime?.message)}
                      </FormErrorMessage>
                    </VStack>
                  </FormControl>
                </HStack>
              </VStack>
              <VStack
                w="full"
                spacing={1}
                borderTopWidth={1}
                borderColor={colors.gray0}
              >
                <HStack w="full" spacing={1}>
                  <NotesIcon fill={colors.blackSecondary} boxSize={5} />
                  <Text
                    fontSize={18}
                    fontWeight={600}
                    color={colors.blackSecondary}
                  >
                    Message
                  </Text>
                  <Text fontSize={14} fontWeight={500} color={colors.grayMain}>
                    (Optional)
                  </Text>
                </HStack>
                <FormControl isInvalid={!!errors?.message}>
                  <Textarea
                    id="subject"
                    h="130px"
                    maxLength={1000}
                    {...register("message", {
                      maxLength: {
                        value: 1000,
                        message: "The message must be 1000 characters or less",
                      },
                    })}
                    resize="none"
                  />
                  <FormErrorMessage>
                    {getMessageFromError(errors?.message?.message)}
                  </FormErrorMessage>
                </FormControl>
              </VStack>
              <VStack w="full" spacing={0} alignItems="flex-start">
                <HStack w="full" spacing={1}>
                  <BellOutlineSlashIcon fill={colors.black} boxSize={5} />
                  <Text
                    fontSize={18}
                    fontWeight={600}
                    color={colors.blackSecondary}
                  >
                    Mute Notifications
                  </Text>
                  <Spacer />
                  <FormControl w="fit-content">
                    <Switch
                      id="isMuteNotifications"
                      size="sm"
                      {...register("isMuteNotifications")}
                    />
                  </FormControl>
                </HStack>
                <Text fontSize={14} fontWeight={400} color={colors.grayMain}>
                  Mute push notifications while away
                </Text>
              </VStack>
              <Divider />
              <Button
                h="40px"
                w="333px"
                type="submit"
                onClick={() => {
                  clearErrors();
                }}
              >
                {isSubmitting ? <Spinner /> : "Save"}
              </Button>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(OutOfOfficeModal);
